import { inject } from 'inversify';

import { STATUS } from '@/constants';
import { UserController } from '@/controllers/UserController';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { Logger } from '@/services/Logger';
import { Signalr } from '@/services/signalr/Signalr';
import { VehicleTypeStore } from '@/stores/VehicleTypeStore';
import { mapValueProperty } from '@/utils/mapValueProperty';
import { orderByField } from '@/utils/orderBy';
import { IVehicleTypeInfo } from '~/api/IVehicleTypeInfo';
import type { IVehicleTypeRespMsg } from '~/api/messages/IVehicleTypeRespMsg';
import type { IVehicleTypeUpdateRespMsg } from '~/api/messages/IVehicleTypeUpdateRespMsg';

@SignalrListener
@service(VehicleTypeManager)
export class VehicleTypeManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(UserController) protected userController: UserController;
  @inject(VehicleTypeStore) protected vehicleTypeStore: VehicleTypeStore;

  find(id?: string) {
    return this.vehicleTypeStore.find(id);
  }

  get all() {
    const vehicleTypes = this.vehicleTypeStore.all()
      .filter((vehicleType) => vehicleType.status !== STATUS.DISABLED);
    return orderByField(vehicleTypes, this.vehicleTypeStore.valueKey);
  }

  reset() {
    this.vehicleTypeStore.set([]);
  }

  updateVehicleTypes(vehicleTypes: IVehicleTypeInfo[]) {
    this.vehicleTypeStore.batchUpdate(mapValueProperty(vehicleTypes));
  }

  @SignalrMessage('VehicleTypeRespMsg')
  protected onVehicleTypeRespMsg(response: IVehicleTypeRespMsg) {
    this.updateVehicleTypes(response.vehicleTypes);
  }

  @SignalrMessage('VehicleTypeUpdateRespMsg')
  protected onVehicleTypeUpdateRespMsg(response: IVehicleTypeUpdateRespMsg) {
    this.updateVehicleTypes([response.vehicleType]);
  }
}
